import { supabase } from "@fieldbook/common/supabase"
import { psInsert } from "@/lib/powersync/crud"
import { queryClient } from "@/lib/queryClient"
import { setTrackShownOnMap } from "@/lib/trackDisplayPrefs"
import {
  claimTrackOwner,
  deleteCompletedTrackGeneration,
  getCompletedTracks,
  getTrackPoints,
  type Track,
  type TrackPoint,
} from "@/lib/tracksDb"
import { getTrackGeoJson } from "./geoJson"

/**
 * Hands saved tracks over from IndexedDB to PowerSync.
 *
 * A track is recorded into IndexedDB (high-frequency point appends, native
 * background capture) and, once the user saves it, becomes a single row in
 * PowerSync's synced `tracks` table. From there PowerSync's own durable
 * upload queue carries it to Supabase and every teammate's device downloads it
 * — nothing here touches the network, so a pass can't fail for want of
 * connectivity.
 *
 * Idempotent and safe to re-run from a cold start: IndexedDB is the only
 * record of what is still to hand over, the PowerSync write is an
 * INSERT OR REPLACE keyed by the track id, and the IndexedDB copy is deleted
 * only after that write has committed. A crash in between just repeats the
 * (identical) write on the next pass.
 */

type TrackRowInput = {
  id: string
  project_id: string
  created_by: string
  geojson: GeoJSON.Feature<GeoJSON.LineString>
  started_at: string
  ended_at: string
  length_m: number
  point_count: number
}

/**
 * The `tracks` row for a saved track. `geojson` is the LineString Feature the
 * web app already reads; per-device state (`displayOnMap`) is left out of it,
 * since it would otherwise be shared with the whole organisation.
 */
export function buildTrackRow(
  track: Track,
  points: TrackPoint[],
  createdBy: string,
): TrackRowInput {
  const feature = getTrackGeoJson(track, points)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { displayOnMap, ...properties } = feature.properties

  return {
    id: track.id,
    project_id: track.projectId,
    created_by: createdBy,
    geojson: { ...feature, properties },
    started_at: new Date(track.timestamp).toISOString(),
    ended_at: new Date(points[points.length - 1].timestamp).toISOString(),
    length_m: feature.properties.lengthM,
    point_count: feature.properties.pointCount,
  }
}

function invalidateTrackQueries(track: Track) {
  queryClient.invalidateQueries({ queryKey: ["tracks", "local"] })
  queryClient.invalidateQueries({ queryKey: ["track", track.id] })
  queryClient.invalidateQueries({ queryKey: ["trackPoints", track.id] })
}

/**
 * Resolve who may publish this track: claim it for `currentUserId` if it's an
 * ownerless legacy track, confirm ownership if it already has one, or return
 * null when it belongs to someone else (or there is no one to claim it).
 */
async function resolveOwnedTrack(
  track: Track,
  currentUserId: string | undefined,
): Promise<Track | null> {
  if (track.ownerId === undefined) {
    // Legacy tracks recorded before per-track ownership existed had no owner
    // at all. Claiming here — rather than leaving them ownerless forever —
    // matches the old upload behaviour, where the persisted mutation just ran
    // under whoever was signed in when it fired. The claim is written before
    // publishing and is permanent (see claimTrackOwner), so a different user
    // can't also claim it later.
    if (!currentUserId) return null
    const claimed = await claimTrackOwner(track.id, currentUserId)
    return claimed && claimed.ownerId === currentUserId ? claimed : null
  }
  return track.ownerId === currentUserId ? track : null
}

async function publishOneTrack(
  track: Track,
  currentUserId: string | undefined,
): Promise<void> {
  // Saved by a build that uploaded straight to Supabase: the server already
  // has it (it reaches this device through the sync stream), so there is
  // nothing to publish. Carry the old "show on map" choice across — keyed by
  // the id the server row ended up with — and drop the local copy.
  if (track.uploadedAt !== undefined) {
    if (track.displayOnMap && track.remoteId) {
      setTrackShownOnMap(track.remoteId, true)
    }
    await deleteCompletedTrackGeneration(track.id, track.completedAt)
    invalidateTrackQueries(track)
    return
  }

  const owned = await resolveOwnedTrack(track, currentUserId)
  // Someone else's track on a shared device, or a legacy track with no one
  // signed in to claim it: leave it alone for its owner's next pass.
  if (!owned || !currentUserId) return

  const points = await getTrackPoints(owned.id)
  // A line needs two points. Anything shorter has nothing worth sharing (and
  // the server never accepted it either), so it is dropped rather than
  // published as an empty track.
  if (points.length >= 2) {
    // A track that was reopened and re-completed reuses its id; the replace
    // makes publishing the newer generation overwrite the older one's row.
    await psInsert("tracks", buildTrackRow(owned, points, currentUserId))
    if (owned.displayOnMap) setTrackShownOnMap(owned.id, true)
  }

  await deleteCompletedTrackGeneration(owned.id, owned.completedAt)
  invalidateTrackQueries(owned)
}

// Pass-level single-flight: only one pass runs at a time. A call that arrives
// while a pass is running doesn't start a second one — it requests one more
// pass after the current one finishes, so a track saved mid-pass is never
// missed.
let activePass: Promise<void> | null = null
let rerunRequested = false

async function runOnePass(): Promise<void> {
  // Read the session once per pass (local and offline-capable) so every
  // track in a pass is judged against the same signed-in user.
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const currentUserId = session?.user.id

  for (const track of await getCompletedTracks()) {
    try {
      await publishOneTrack(track, currentUserId)
    } catch (error) {
      // The track stays in IndexedDB and is retried on the next trigger.
      console.warn(`Failed to publish track ${track.id}`, error)
    }
  }
}

async function runPasses(): Promise<void> {
  do {
    rerunRequested = false
    try {
      await runOnePass()
    } catch (error) {
      // Reading the session or IndexedDB failed outright. Nothing is lost —
      // the tracks are still in IndexedDB for the next trigger — but a
      // fire-and-forget caller must not see an unhandled rejection.
      console.warn("Failed to publish completed tracks", error)
    }
  } while (rerunRequested)
}

/**
 * Hand every saved-but-unpublished local track to PowerSync. Fire-and-forget
 * friendly: callers that don't need to know when it settles can `void` it.
 */
export function publishCompletedTracks(): Promise<void> {
  if (activePass) {
    rerunRequested = true
    return activePass
  }

  activePass = runPasses().finally(() => {
    activePass = null
  })
  return activePass
}
